import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, TextField, Select, MenuItem, InputLabel,
  FormControl, Snackbar, Alert, CircularProgress, InputAdornment,
  Tooltip, IconButton, Chip, Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  X, Save, Trash2, Building, Smartphone, Search, AlertTriangle, XCircle, CheckCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import api from '../services/api';

const TabletManagementPage = () => {
  const [devices, setDevices] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    hotel_id: '',
    device_name: '',
    status: 'active'
  });
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  const theme = useTheme();
  const searchTimeoutRef = useRef(null);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch devices with search
  const fetchDevices = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = searchTerm.trim() ? { search: searchTerm.trim() } : {};
      const response = await api.get('/tablet-devices', { params });
      setDevices(response.data || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showSnackbar('Error loading devices', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch hotels
  const fetchHotels = useCallback(async () => {
    try {
      const response = await api.get('/admin/hotels');
      setHotels(response.data || []);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      showSnackbar('Error loading hotels', 'error');
    }
  }, []);

  // Load initial data
  useEffect(() => {
    fetchDevices();
    fetchHotels();
  }, [fetchDevices, fetchHotels]);

  // Search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchDevices(search);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search, fetchDevices]);

  // Open edit dialog
  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      hotel_id: device.hotel_id || '',
      device_name: device.device_name || '',
      status: device.status || 'active'
    });
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDevice(null);
    setFormData({
      hotel_id: '',
      device_name: '',
      status: 'active'
    });
  };

  // Save changes
  const handleSave = async () => {
    if (!editingDevice) return;

    try {
      await api.put(`/tablet-devices/${editingDevice.id}`, formData);
      showSnackbar('Device updated successfully');
      handleCloseDialog();
      fetchDevices(search);
    } catch (error) {
      console.error('Error updating device:', error);
      showSnackbar(
        error.response?.data?.msg || 'Error updating device', 
        'error'
      );
    }
  };

  // Delete device
  const handleDelete = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) {
      return;
    }

    try {
      await api.delete(`/tablet-devices/${deviceId}`);
      showSnackbar('Device deleted successfully');
      fetchDevices(search);
    } catch (error) {
      console.error('Error deleting device:', error);
      showSnackbar(
        error.response?.data?.msg || 'Error deleting device', 
        'error'
      );
    }
  };

  // Remove hotel assignment
  const handleUnassign = async (deviceId) => {
    if (!window.confirm('Remove hotel assignment from this device?')) {
      return;
    }

    try {
      await api.put(`/tablet-devices/${deviceId}`, {
        hotel_id: null,
        device_name: devices.find(d => d.id === deviceId)?.device_name || '',
        status: 'active'
      });
      showSnackbar('Hotel assignment removed');
      fetchDevices(search);
    } catch (error) {
      console.error('Error unassigning device:', error);
      showSnackbar('Error removing assignment', 'error');
    }
  };

  // Get device status chip
  const getStatusChip = (device) => {
    if (!device.hotel_id) {
      return (
        <Chip
          icon={<AlertTriangle size={14} />}
          label="Unassigned"
          size="small"
          sx={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            '& .MuiChip-icon': { color: '#856404' }
          }}
        />
      );
    }

    if (device.status === 'inactive') {
      return (
        <Chip
          icon={<XCircle size={14} />}
          label="Inactive"
          size="small"
          sx={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            '& .MuiChip-icon': { color: '#721c24' }
          }}
        />
      );
    }

    return (
      <Chip
        icon={<CheckCircle size={14} />}
        label="Active"
        size="small"
        sx={{
          backgroundColor: '#d1edff',
          color: '#0c5460',
          '& .MuiChip-icon': { color: '#0c5460' }
        }}
      />
    );
  };

  return (
    <Box p={1}>
      {/* Top Bar */}
      <Box sx={{ 
        pb: 1, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        alignItems: 'center', 
        gap: 1, 
        maxWidth: 1000, 
        margin: '0 auto' 
      }}>
        <TextField
          size='small' 
          placeholder="Search device ID, name, or hotel..."
          value={search} 
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          sx={{ 
            flex: 1,
            backgroundColor: '#fff', 
            borderRadius: 5, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
            width: { xs: '100%', sm: 'auto' }, 
            '& .MuiOutlinedInput-root': { 
              '& fieldset': { border: 'none' } 
            } 
          }}
          InputProps={{ 
            startAdornment: ( 
              <InputAdornment position="start">
                <Search size={18} color="#757575"/>
              </InputAdornment> 
            ),
            endAdornment: search ? ( 
              <InputAdornment position="end">
                <IconButton onClick={() => setSearch('')} size="small" edge="end">
                  <X size={16} />
                </IconButton>
              </InputAdornment> 
            ) : null,
          }}
        />
      </Box>

      {/* Main Table */}
      <Box sx={{ 
        overflowX: 'auto', 
        borderRadius: 2, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
        maxWidth: 1000, 
        margin: '0 auto', 
        width: '100%' 
      }}>
        <Paper sx={{ 
          backgroundColor: '#fff', 
          borderRadius: 2, 
          boxShadow: 3, 
          overflow: 'hidden' 
        }}>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: '#f0f0f0', fontWeight: 'bold', whiteSpace: 'nowrap' } }}>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Device Name</TableCell>
                  <TableCell>Hotel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !loading && devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No devices found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  devices.map((device) => (
                    <TableRow 
                      hover 
                      key={device.id}
                      onClick={() => handleEdit(device)}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'rgba(0, 0, 0, 0.04)', 
                          cursor: 'pointer' 
                        } 
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Smartphone size={16} color="#1976d2" />
                          <Typography variant="body2" component="span">
                            {device.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {device.device_name || (
                          <Typography variant="body2" color="text.secondary">
                            No name
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.hotel_name ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Building size={14} color="#666" />
                            {device.hotel_name}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Edit/Add Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)' 
          } 
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 'bold', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          pb: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Smartphone size={24} color="#1976d2" />
            <span>
              {editingDevice ? `Edit Device ${editingDevice.id}` : 'Add New Device'}
            </span>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1} sx={{ color: '#333' }}>
              Device Information
            </Typography>

            <TextField
              label="Device Name"
              value={formData.device_name}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                device_name: e.target.value
              }))}
              fullWidth
              placeholder="e.g., Front Desk Tablet"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Smartphone size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth>
              <InputLabel id="hotel-select-label">Hotel Assignment</InputLabel>
              <Select
                labelId="hotel-select-label"
                value={formData.hotel_id}
                label="Hotel Assignment"
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  hotel_id: e.target.value
                }))}
                startAdornment={
                  <InputAdornment position="start">
                    <Building size={18} color="#666" />
                  </InputAdornment>
                }
              >
                <MenuItem value="">
                  <em>No hotel assigned</em>
                </MenuItem>
                {hotels.filter(h => h.status === 'active').map((hotel) => (
                  <MenuItem key={hotel.id} value={hotel.id}>
                    {hotel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  status: e.target.value
                }))}
                startAdornment={
                  <InputAdornment position="start">
                    <CheckCircle size={18} color="#666" />
                  </InputAdornment>
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        {/* Dialog Actions */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={handleCloseDialog} 
            startIcon={<X size={18} />} 
            fullWidth 
            sx={{ 
              fontWeight: 'bold', 
              backgroundColor: '#f5f5f5', 
              color: '#333', 
              border: '1px solid #ddd', 
              '&:hover': { backgroundColor: '#e0e0e0' } 
            }}
          >
            Cancel
          </Button>
          
          {editingDevice && (
            <>
              <Button 
                onClick={handleSave} 
                startIcon={<Save size={18} />} 
                fullWidth 
                sx={{ 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(to right, #1976d2, #0d47a1)', 
                  color: '#fff', 
                  '&:hover': { 
                    background: 'linear-gradient(to right, #1565c0, #003c8f)' 
                  } 
                }}
              >
                Update Device
              </Button>

              {editingDevice.hotel_id && (
                <Button 
                  onClick={() => {
                    handleUnassign(editingDevice.id);
                    handleCloseDialog();
                  }}
                  startIcon={<Building size={18} />} 
                  fullWidth 
                  sx={{ 
                    fontWeight: 'bold', 
                    background: 'linear-gradient(to right, #f57c00, #e65100)', 
                    color: '#fff', 
                    '&:hover': { 
                      background: 'linear-gradient(to right, #ef6c00, #d84315)' 
                    } 
                  }}
                >
                  Remove Hotel Assignment
                </Button>
              )}

              <Button 
                onClick={() => {
                  handleDelete(editingDevice.id);
                  handleCloseDialog();
                }}
                startIcon={<Trash2 size={18} />} 
                fullWidth 
                sx={{ 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(to right, #f44336, #c62828)', 
                  color: '#fff', 
                  '&:hover': { 
                    background: 'linear-gradient(to right, #e53935, #b71c1c)' 
                  } 
                }}
              >
                Delete Device
              </Button>
            </>
          )}
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TabletManagementPage;